import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { BreakpointObserverService } from './shared/services/breakpoint-observer.service';
import { LayersMenuService } from './map/menu/right-menu/layers-menu/layers-menu.service';
import { AuthenticationService } from './user/authentication.service';
import { catchError, of, take } from 'rxjs';
import { setSentryUser } from './user/user/user.util';
import { UserAccessService } from './user/access/user-access.service';
import { ModalService } from './shared/services/modal.service';
import { NavigationMenuComponent } from './shared/components/navigation-menu/navigation-menu.component';
import { AsyncPipe, NgIf } from '@angular/common';
import { IconRegistryService } from './shared/services/icon-registry.service';
import { LastVisitService } from './shared/services/last-visit.service';
import { captureTrafficSource } from './sales-forecast/sales-forecast-funnel.service';
import { GoogleAnalyticsService } from './user/google-analytics.service';
import { replayPendingPurchaseConversion } from './user/user/subscriptions-menu/pending-purchase-conversion';

export const REDIRECT_AFTER_LOGIN_PATH = 'REDIRECT_AFTER_LOGIN_PATH';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    NavigationMenuComponent,
    NgIf,
    AsyncPipe,
    RouterModule,
  ],
})
export class AppComponent implements OnInit {
  title = 'map-ai';

  constructor(
    private router: Router,
    public breakpointObserverService: BreakpointObserverService,
    public menuService: LayersMenuService,
    private authService: AuthenticationService,
    private accessService: UserAccessService,
    private modalService: ModalService,
    private iconRegistry: IconRegistryService,
    private ga: GoogleAnalyticsService,
    // Simply inject to run services inner event handlers
    private lastVisit: LastVisitService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.iconRegistry.initUtilityIcons();
  }

  ngOnInit() {
    // Everything here describes a real visitor arriving, so none of it belongs
    // in a server render. startPageView() is the one that would do damage
    // rather than merely waste work: it POSTs a `site-opened` row, and
    // prerendering would write one per page at build time, from CI, into the
    // same user_actions_log the funnel is measured from.
    if (isPlatformBrowser(this.platformId)) {
      // Before anything can rewrite the URL, and regardless of which page the
      // ad pointed at - the funnel only reads the URL once a sales-forecast
      // page view starts, which is too late for a click that landed elsewhere.
      captureTrafficSource();

      // A purchase this browser recorded but never saw gtag send - the buyer
      // closed the Stripe success tab before the hit went out. Retried here
      // rather than on the subscription page, because that tab is gone and
      // this runs wherever they land next; both events carry the Stripe
      // session as transaction_id, so a duplicate deduplicates itself.
      replayPendingPurchaseConversion(this.ga);

      this.accessService.startPageView();
    }

    // Server-safe on its own: getTokens() reads as signed out off-browser, so
    // this returns without touching the router or the network.
    this.handleIsLoggedInSubscription();

    if (isPlatformBrowser(this.platformId)) {
      this.preventPinchZoomOutsideMap();
    }
    // this.orientationService.detectOrientation();
  }

  private preventPinchZoomOutsideMap(): void {
    document.addEventListener(
      'touchmove',
      (e: TouchEvent) => {
        if (e.touches.length > 1) {
          const target = e.target as Node;
          const insideMap = ['map', 'chat-map'].some((id) =>
            document.getElementById(id)?.contains(target)
          );
          if (!insideMap) {
            e.preventDefault();
          }
        }
      },
      { passive: false }
    );
  }

  public isAllowedUnauthorized(): boolean {
    const currentUrl = this.router.url;
    const allowedPaths = [
      'password-recovery',
      'user/password/reset',
      'sign-up',
      'sign-in',
      'sign-up/individual',
      'sign-up/enterprise',
      'info',
    ];

    // Handle case when user is logged in and resets password, this way we show him sidenav and do not redirect from reset page
    if (
      currentUrl.includes('user/password/reset') &&
      this.authService.getUserEmail()
    ) {
      return false;
    }

    return allowedPaths.some((path) => currentUrl.includes(path));
  }

  private handleIsLoggedInSubscription(): void {
    if (this.authService.hasAccessToken()) {
      this.authService.userPersonalData
        .pipe(
          take(1),
          catchError((err) => {
            if (this.isAllowedUnauthorized()) {
              return of(null);
            }

            this.router.navigate(['sign-in']);
            this.authService.logout();
            throw err;
          })
        )
        .subscribe((data) => {
          if (data) {
            this.accessService.setUserPlan(data);
            setSentryUser(data);
            if (this.isAllowedUnauthorized()) {
              this.router.navigate(['/']);
            }
          }
        });

      this.authService.refreshUserInfo();
    }
  }
}
